/**
 * TripListPanel
 * Återanvändbar tripplista (utan karta). Visar registrerade trippar eller trippar
 * detekterade från bin-fil. Render-mönster hämtat från reports/TripsView.tsx.
 */
import React from 'react';
import { useTranslation } from '@/store/languageStore';

export interface PanelTrip {
  id: string;
  startTime: string;
  endTime?: string;
  distanceM: number;
  durationSec?: number;
  averageSpeed?: number;
  maxSpeedKmh?: number;
  startAddress?: string;
  endAddress?: string;
  pointCount?: number;
  hasAvl250?: boolean;
  isPrivate?: boolean;
  status?: string;
  reconstructed?: boolean;
  distanceSource?: 'gps' | 'oem' | 'osrm' | 'straight';
}

interface TripListPanelProps {
  title: string;
  trips: PanelTrip[];
  variant: 'registered' | 'bin';
  loading?: boolean;
  emptyLabel: string;
  /** Extra rad överst i panelen (t.ex. knappar). */
  headerExtra?: React.ReactNode;
}

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat('sv-SE', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));

const formatTime = (iso: string) =>
  new Intl.DateTimeFormat('sv-SE', { timeStyle: 'short' }).format(new Date(iso));

const formatDuration = (seconds?: number) => {
  if (seconds === undefined || seconds === null) return '-';
  if (seconds <= 0) return '0h 0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

const formatDistance = (meters: number) => {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(2)} km`;
};

const sameLocalCalendarDay = (startIso: string, endIso?: string): boolean => {
  if (!endIso) return false;
  const a = new Date(startIso);
  const b = new Date(endIso);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const truncateAddress = (address?: string, maxLength = 35) => {
  if (!address) return null;
  return address.length <= maxLength ? address : address.substring(0, maxLength) + '...';
};

const TripListPanel: React.FC<TripListPanelProps> = ({ title, trips, variant, loading, emptyLabel, headerExtra }) => {
  const { t } = useTranslation();

  return (
    <div className="bg-bgElevated shadow rounded-lg flex-1 flex flex-col min-h-0">
      <div className="p-4 border-b border-border flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-textDefault">
          {title} <span className="text-textSecondary font-normal">({trips.length})</span>
        </h2>
        {headerExtra}
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-textSecondary">{t('common.loading') || 'Laddar...'}</div>
        ) : trips.length === 0 ? (
          <div className="p-4 text-center text-textSecondary">{emptyLabel}</div>
        ) : (
          <div className="divide-y divide-border">
            {trips.map((trip) => (
              <div key={trip.id} className="w-full p-4 text-left">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-textDefault">
                      {formatDate(trip.startTime)}
                      {trip.endTime && (
                        <span className="text-textSecondary font-normal">
                          {' → '}
                          {sameLocalCalendarDay(trip.startTime, trip.endTime)
                            ? formatTime(trip.endTime)
                            : formatDate(trip.endTime)}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-textSecondary mt-1">
                      {formatDistance(trip.distanceM)} • {formatDuration(trip.durationSec)}
                      {variant === 'registered' && trip.averageSpeed
                        ? ` • ${Math.round(trip.averageSpeed)} km/h`
                        : ''}
                      {variant === 'bin' && trip.maxSpeedKmh !== undefined
                        ? ` • ${t('tripAnalysis.maxSpeed') || 'max'} ${trip.maxSpeedKmh} km/h`
                        : ''}
                    </div>
                    {trip.isPrivate && (
                      <div className="mt-2 px-2 py-1 bg-bgContainer rounded text-xs text-textTertiary italic">
                        {t('trips.privateHidden') || 'Privat resa'}
                      </div>
                    )}
                    {!trip.isPrivate && (trip.startAddress || trip.endAddress) && (
                      <div className="text-xs text-textSecondary mt-2 space-y-0.5">
                        {trip.startAddress && (
                          <div className="flex items-start">
                            <span className="text-green-500 mr-1">●</span>
                            <span className="truncate" title={trip.startAddress}>
                              {truncateAddress(trip.startAddress)}
                            </span>
                          </div>
                        )}
                        {trip.endAddress && (
                          <div className="flex items-start">
                            <span className="text-red-500 mr-1">●</span>
                            <span className="truncate" title={trip.endAddress}>
                              {truncateAddress(trip.endAddress)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    {trip.pointCount !== undefined && trip.pointCount > 0 && (
                      <div className="text-xs text-textTertiary mt-1">
                        {trip.pointCount} {t('tripAnalysis.gpsPoints') || 'GPS-punkter'}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 ml-2 flex-shrink-0">
                    {trip.reconstructed && (
                      <span
                        className="px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                        title={
                          (t('tripAnalysis.reconstructedHint') ||
                            'Rekonstruerad tripp (GPS saknades) - sträcka: ') +
                          (trip.distanceSource === 'oem'
                            ? 'OEM-mätare'
                            : trip.distanceSource === 'osrm'
                              ? 'OSRM-uppskattning'
                              : 'fågelväg')
                        }
                      >
                        {t('tripAnalysis.reconstructed') || 'Rekonstruerad'}
                        {trip.distanceSource === 'oem' ? ' (OEM)' : trip.distanceSource === 'osrm' ? ' (~)' : ''}
                      </span>
                    )}
                    {variant === 'registered' && trip.status === 'completed' && (
                      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded">
                        {t('trips.completed') || 'Klar'}
                      </span>
                    )}
                    {variant === 'bin' && (
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${
                          trip.hasAvl250
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }`}
                      >
                        {trip.hasAvl250 ? 'AVL250' : t('tripAnalysis.noAvl250') || 'Ingen AVL250'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TripListPanel;
